import { DroppedItem } from "../../pages/Home/types";
import { getAngleFromCenter, getAngleFromCenterXY } from "./getAngleFromCentre";

/**
 * Determines which direction an angle belongs to based on a set of direction ranges
 * @param angle - The angle to check in degrees (0-360)
 * @param directions - Array of direction objects with start and end properties
 * @param rotation - Rotation offset to apply to the angle
 * @returns The matching direction object or null if no match
 */
export function getDirectionByAngle(
  angle: number,
  directions: any,
  rotation: any
): any | null {
  // Filter out the central "bramhasthan" direction
  directions = directions?.filter(
    (dir: any) => dir.systemName !== "bramhasthan"
  );

  // Adjust angle based on rotation
  angle = (angle - rotation + 360) % 360;

  // Find which direction contains this angle
  for (const dir of directions) {
    const { start, end } = dir;

    if (start < end) {
      // Normal range (e.g. 45° to 90°)
      if (angle >= start && angle < end) {
        return dir;
      }
    } else {
      // Wrapped range (e.g. 315° to 45°)
      if (angle >= start || angle < end) {
        return dir;
      }
    }
  }

  return null;
}

/**
 * Converts a corner position to screen coordinates with rotation
 * @param itemCenterScreen - Center of the item in screen coordinates
 * @param halfWidth - Half of the item's width
 * @param halfHeight - Half of the item's height
 * @param rotationDeg - Rotation angle in degrees
 * @param cornerSignX - Direction modifier for X (-1 for left, 1 for right)
 * @param cornerSignY - Direction modifier for Y (-1 for top, 1 for bottom)
 * @returns Screen coordinates for the rotated corner
 */
function getCornerScreenCoord(
  itemCenterScreen: { x: number; y: number },
  halfWidth: number,
  halfHeight: number,
  rotationDeg: number,
  cornerSignX: 1 | -1,
  cornerSignY: 1 | -1
): { x: number; y: number } {
  // Local corner coords before rotation
  const localX = cornerSignX * halfWidth;
  const localY = cornerSignY * halfHeight;

  // Rotate local corner around (0,0)
  const theta = (Math.PI / 180) * rotationDeg;
  const rotatedX = localX * Math.cos(theta) - localY * Math.sin(theta);
  const rotatedY = localX * Math.sin(theta) + localY * Math.cos(theta);

  // Translate to the item's center position
  return {
    x: itemCenterScreen.x + rotatedX,
    y: itemCenterScreen.y + rotatedY,
  };
}

/**
 * Converts pixel coordinates (relative to map viewport) back into geographic coordinates
 * @param map - The Google Map instance
 * @param x - Horizontal pixel coordinate (relative to map's left edge)
 * @param y - Vertical pixel coordinate (relative to map's top edge)
 * @returns Google LatLng object or null if conversion fails
 */
export function getCornerLatLng(
  map: google.maps.Map,
  x: number,
  y: number
): google.maps.LatLng | null {
  const projection = map.getProjection();
  if (!projection) return null;

  const bounds = map.getBounds();
  if (!bounds) return null;

  const zoom = map.getZoom();
  if (zoom == null) return null;

  // Calculate scale for current zoom level
  const scale = Math.pow(2, zoom);

  // Find the top-left corner of the map viewport
  const topLeftLatLng = new google.maps.LatLng(
    bounds.getNorthEast().lat(), // top
    bounds.getSouthWest().lng() // left
  );
  const topLeftWorld = projection.fromLatLngToPoint(topLeftLatLng);
  if (!topLeftWorld) return null;

  // Calculate the world point by offsetting from the top-left
  const worldPoint = new google.maps.Point(
    topLeftWorld.x + x / scale,
    topLeftWorld.y + y / scale
  );

  // Convert world point back to lat/lng
  const latLng = projection.fromPointToLatLng(worldPoint);
  return latLng || null;
}

/**
 * Converts geographic coordinates to pixel coordinates on the map viewport
 * @param lat - Latitude
 * @param lng - Longitude
 * @param map - The Google Map instance
 * @returns Pixel coordinates relative to the top-left of the map viewport
 */
export function latLngToPixel(
  lat: number,
  lng: number,
  map: google.maps.Map
): { x: number; y: number } {
  const latLng = new google.maps.LatLng(lat, lng);
  const projection = map.getProjection();
  const bounds = map.getBounds();
  const zoom = map.getZoom();

  if (!projection || !bounds || zoom == null) {
    return { x: 0, y: 0 };
  }

  // Get scale factor for the current zoom level
  const scale = Math.pow(2, zoom);

  // Convert lat/lng to world coordinates
  const worldPoint = projection.fromLatLngToPoint(latLng);
  if (!worldPoint) {
    return { x: 0, y: 0 };
  }

  // Get reference point for the top-left of the map viewport
  const topLeftLatLng = new google.maps.LatLng(
    bounds.getNorthEast().lat(), // top
    bounds.getSouthWest().lng() // left
  );
  const topLeftWorld = projection.fromLatLngToPoint(topLeftLatLng);
  if (!topLeftWorld) {
    return { x: 0, y: 0 };
  }

  // Calculate pixel coordinates relative to top-left viewport
  const x = (worldPoint.x - topLeftWorld.x) * scale;
  const y = (worldPoint.y - topLeftWorld.y) * scale;

  return { x, y };
}

/**
 * Gets all four corners of an item in geographic coordinates
 * @param item - The item with position and dimensions
 * @param map - The Google Map instance
 * @returns Array of LatLng objects for each corner
 */
function getItemCornersLatLng(item: DroppedItem, map: google.maps.Map) {
  // Find the screen coordinates of the item center
  const lat = item.lat ?? 0;
  const lng = item.lng ?? 0;
  const { x: centerX, y: centerY } = latLngToPixel(lat, lng, map);

  const width = item.width ?? 0;
  const height = item.height ?? 0;
  const halfW = width / 2;
  const halfH = height / 2;

  // Calculate the four corners in screen coordinates (top-left, top-right, bottom-left, bottom-right)
  const rotation = item.rotation ?? 0;
  const cornersScreen = [
    getCornerScreenCoord(
      { x: centerX, y: centerY },
      halfW,
      halfH,
      rotation,
      -1,
      -1
    ),
    getCornerScreenCoord(
      { x: centerX, y: centerY },
      halfW,
      halfH,
      rotation,
      1,
      -1
    ),
    getCornerScreenCoord(
      { x: centerX, y: centerY },
      halfW,
      halfH,
      rotation,
      -1,
      1
    ),
    getCornerScreenCoord(
      { x: centerX, y: centerY },
      halfW,
      halfH,
      rotation,
      1,
      1
    ),
  ];

  // Convert screen coordinates to geographic coordinates
  return cornersScreen.map(({ x, y }) => getCornerLatLng(map, x, y));
}

/**
 * Determines which directions an item covers based on its corners
 * @param item - The item to check
 * @param map - The Google Map instance
 * @param compassCenter - Center point for angle calculations
 * @param allDirections - Array of all possible directions
 * @returns Array of direction names that the item covers
 */
export function getDirectionsForItem(
  item: any,
  map: google.maps.Map,
  compassCenter: { lat: number; lng: number },
  allDirections: any[]
): string[] {
  // Get the item's corners and calculate their angles from center
  const corners = getItemCornersLatLng(item, map);
  const angles: number[] = [];

  corners.forEach((corner) => {
    if (!corner) return;
    const angle = getAngleFromCenter(compassCenter, {
      lat: corner.lat(),
      lng: corner.lng(),
    });
    angles.push(angle);
  });

  if (angles.length === 0) return [];

  // Sort angles and find the bounding arc that covers all corners
  angles.sort((a, b) => a - b);

  // Add first angle + 360 to handle wrap-around cases
  const extended = [...angles, angles[0] + 360];
  let largestGap = -1;
  let largestGapIndex = 0;

  // Find the largest gap between consecutive angles
  for (let i = 0; i < angles.length; i++) {
    const gap = extended[i + 1] - extended[i];
    if (gap > largestGap) {
      largestGap = gap;
      largestGapIndex = i;
    }
  }

  // The arc covering all angles is the complement of the largest gap
  const arcStart = extended[largestGapIndex + 1] % 360;
  const arcEnd = angles[largestGapIndex];

  // Filter out bramhasthan if needed
  const directions = allDirections.filter(
    (d) => d.systemName !== "bramhasthan"
  );

  // Check which directions intersect with the item's arc
  const directionsCovered = new Set<string>();

  for (const dir of directions) {
    const { start, end, id } = dir;
    if (rangeIntersectsArc(start, end, arcStart, arcEnd)) {
      directionsCovered.add(id);
    }
  }

  return Array.from(directionsCovered);
}

/**
 * Checks if a direction's range overlaps with an arc
 * @param dirStart - Direction start angle
 * @param dirEnd - Direction end angle
 * @param arcStart - Arc start angle
 * @param arcEnd - Arc end angle
 * @returns True if there's any overlap
 */
function rangeIntersectsArc(
  dirStart: number,
  dirEnd: number,
  arcStart: number,
  arcEnd: number
): boolean {
  // Normalize angles to 0-360 range
  dirStart = ((dirStart % 360) + 360) % 360;
  dirEnd = ((dirEnd % 360) + 360) % 360;
  arcStart = ((arcStart % 360) + 360) % 360;
  arcEnd = ((arcEnd % 360) + 360) % 360;

  // Check if either endpoint of the direction is in the arc
  if (
    angleInArc(dirStart, arcStart, arcEnd) ||
    angleInArc(dirEnd, arcStart, arcEnd)
  ) {
    return true;
  }

  // Check if either endpoint of the arc is in the direction range
  if (
    angleInArc(arcStart, dirStart, dirEnd) ||
    angleInArc(arcEnd, dirStart, dirEnd)
  ) {
    return true;
  }

  return false;
}

/**
 * Checks if an angle is within an arc range
 * @param angle - The angle to check
 * @param arcStart - Start of the arc
 * @param arcEnd - End of the arc
 * @returns True if the angle is within the arc
 */
function angleInArc(angle: number, arcStart: number, arcEnd: number): boolean {
  // Normalize angles to 0-360 range
  angle = ((angle % 360) + 360) % 360;
  arcStart = ((arcStart % 360) + 360) % 360;
  arcEnd = ((arcEnd % 360) + 360) % 360;

  if (arcStart <= arcEnd) {
    // Normal range (e.g. 45° to 90°)
    return angle >= arcStart && angle <= arcEnd;
  } else {
    // Wrapped range (e.g. 315° to 45°)
    return angle >= arcStart || angle <= arcEnd;
  }
}

export function getDirectionsForItemXY(
  item: DroppedItem,
  compassCenter: { x: number; y: number },
  allDirections: any[],
  planRotation: number
): string[] {
  console.log("hi");
  const width = item.width ?? 0;
  const height = item.height ?? 0;
  const rotation = item.rotation ?? 0;

  const halfW = width / 2;
  const halfH = height / 2;

  const itemX = item.x ?? 0;
  const itemY = item.y ?? 0;

  const corners = [
    getCornerXY(itemX, itemY, halfW, halfH, rotation, -1, -1),
    getCornerXY(itemX, itemY, halfW, halfH, rotation, +1, -1),
    getCornerXY(itemX, itemY, halfW, halfH, rotation, -1, +1),
    getCornerXY(itemX, itemY, halfW, halfH, rotation, +1, +1),
  ];

  console.log(corners);

  // Convert each corner to angle from compassCenter => angles[] (0..360)
  let angles = corners.map((corner) =>
    getAngleFromCenterXY(compassCenter, corner)
  );

  angles = angles.map((a) => (a - planRotation + 360) % 360);

  // If no corners, return []
  if (!angles.length) return [];

  // Sort angles ascending and add (angle[0] + 360) to handle wrap
  angles.sort((a, b) => a - b);
  const extended = [...angles, angles[0] + 360];

  // Find largest gap => the arc is the complement
  let largestGap = -1;
  let largestGapIndex = 0;
  for (let i = 0; i < angles.length; i++) {
    const gap = extended[i + 1] - extended[i];
    if (gap > largestGap) {
      largestGap = gap;
      largestGapIndex = i;
    }
  }

  // arc covers all angles => from extended[largestGapIndex+1] .. angles[largestGapIndex]
  const arcStart = extended[largestGapIndex + 1] % 360;
  const arcEnd = angles[largestGapIndex];

  // Filter out bramhasthan 
  const directions = allDirections.filter(
    (d: any) => d.systemName !== "bramhasthan"
  );

  //  For each direction, see if [dir.start, dir.end] intersects with [arcStart, arcEnd]
  const directionsCovered = new Set<string>();
  for (const dir of directions) {
    const { start, end, id } = dir;
    if (rangeIntersectsArc(start, end, arcStart, arcEnd)) {
      directionsCovered.add(id);
    }
  }

  return [...directionsCovered];
}

function getCornerXY(
  centerX: number,
  centerY: number,
  halfWidth: number,
  halfHeight: number,
  rotationDeg: number,
  cornerSignX: 1 | -1,
  cornerSignY: 1 | -1
): { x: number; y: number } {
  // local corner (before rotation)
  const localX = cornerSignX * halfWidth;
  const localY = cornerSignY * halfHeight;

  // Rotate around (0,0):
  const theta = (Math.PI / 180) * rotationDeg;
  const rotatedX = localX * Math.cos(theta) - localY * Math.sin(theta);
  const rotatedY = localX * Math.sin(theta) + localY * Math.cos(theta);

  // Translate back to item center
  return {
    x: centerX + rotatedX,
    y: centerY + rotatedY,
  };
}
